/**
 * Converts a string to a URL-friendly slug.
 * - Converts to lowercase (unless disabled)
 * - Replaces non-alphanumeric characters with the separator
 * - Removes leading separators
 * - Removes trailing separators (unless preserveSeparator is true)
 * - Collapses multiple consecutive separators into one
 *
 * @param text - The text to convert to a slug
 * @param options - Optional configuration
 * @param options.separator - Character to use as separator (default: "-")
 * @param options.lowercase - Whether to convert to lowercase (default: true)
 * @param options.preserveSeparator - Whether to preserve existing separator characters in input and allow trailing separators (default: false).
 *                                     Useful for live editing where users are typing hyphens.
 * @returns URL-friendly slug
 *
 * @example
 * ```typescript
 * slugify("Hello World");              // "hello-world"
 * slugify("My New Page!");             // "my-new-page"
 * slugify("  Trim Spaces  ");          // "trim-spaces"
 * slugify("Multiple   Spaces");        // "multiple-spaces"
 * slugify("Special @#$ Characters");   // "special-characters"
 * slugify("Hello World", { separator: "_" }); // "hello_world"
 * slugify("Hello World", { lowercase: false }); // "Hello-World"
 *
 * // preserveSeparator: true allows hyphens to be typed and preserved
 * slugify("hello-world", { preserveSeparator: true }); // "hello-world"
 * slugify("hello-", { preserveSeparator: true });      // "hello-" (trailing hyphen preserved)
 * slugify("h-", { preserveSeparator: true });          // "h-" (trailing hyphen preserved)
 * ```
 */
export function slugify(
  text: string,
  options: { separator?: string; lowercase?: boolean; preserveSeparator?: boolean } = {}
): string {
  const { separator = "-", lowercase = true, preserveSeparator = false } = options;

  let slug = text;

  // Convert to lowercase if requested
  if (lowercase) {
    slug = slug.toLowerCase();
  }

  // Escape separator for regex if it's a special regex character
  const escapedSeparator = separator.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Replace non-alphanumeric characters with the separator
  // If preserveSeparator is true, keep existing separator characters
  const pattern = preserveSeparator
    ? new RegExp(`[^a-zA-Z0-9${escapedSeparator}]+`, "g")
    : new RegExp(`[^a-zA-Z0-9]+`, "g");
  slug = slug.replace(pattern, separator);

  // Collapse multiple consecutive separators into one
  if (preserveSeparator) {
    const collapsePattern = new RegExp(`${escapedSeparator}+`, "g");
    slug = slug.replace(collapsePattern, separator);
  }

  // Remove leading separators (always)
  const leadingPattern = new RegExp(`^${escapedSeparator}+`, "g");
  slug = slug.replace(leadingPattern, "");

  // Remove trailing separators only if not preserving separators
  // (when preserving, allow trailing separators for live editing)
  if (!preserveSeparator) {
    const trailingPattern = new RegExp(`${escapedSeparator}+$`, "g");
    slug = slug.replace(trailingPattern, "");
  }

  return slug;
}
